/**
 * AgentThinkingIndicator — animated neural-activity indicator shown while
 * the agent is processing before any content streams in.
 *
 * StreamingCursor — blinking beam shown at the end of partial assistant
 * content while the SSE stream is still open.
 *
 * Both components lazily inject a single shared <style> block into
 * document.head on first mount (client-only, no SSR concerns).
 */

import { useEffect, useState } from 'react';

/* ─── Phases shown while thinking ─── */
const PHASES = [
  'Thinking…',
  'Reasoning…',
  'Composing…',
  'Crafting…',
  'Analyzing…',
];

/* ─── CSS injection ─── */
const STYLE_ID = '__ng_thinking__';

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;

  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes _ng_wave {
      0%, 55%, 100% { transform: translateY(0) scale(1);    opacity: 0.4; }
      27%            { transform: translateY(-7px) scale(1.25); opacity: 1;   }
    }
    @keyframes _ng_shimmer {
      0%   { left: -65%; }
      100% { left: 125%; }
    }
    @keyframes _ng_enter {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0);   }
    }
    @keyframes _ng_blink {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0; }
    }
    @keyframes _ng_glow_pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(129,140,248,0); }
      50%      { box-shadow: 0 0 8px 2px rgba(129,140,248,0.35); }
    }

    ._ng_enter   { animation: _ng_enter 0.22s ease-out both; }
    ._ng_d1      { animation: _ng_wave  1.45s ease-in-out infinite   0ms; }
    ._ng_d2      { animation: _ng_wave  1.45s ease-in-out infinite 160ms; }
    ._ng_d3      { animation: _ng_wave  1.45s ease-in-out infinite 320ms; }
    ._ng_shimmer {
      position: absolute; inset: 0; width: 55%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(129,140,248,0.75) 35%,
        rgba(45,212,191,0.75)  65%,
        transparent
      );
      animation: _ng_shimmer 1.75s ease-in-out infinite;
    }
    ._ng_cursor {
      display: inline-block;
      width: 2px;
      height: 0.85em;
      border-radius: 2px;
      margin-left: 3px;
      vertical-align: text-bottom;
      background: #818cf8;
      animation: _ng_blink 1s step-end infinite;
    }
    ._ng_orb_glow {
      animation: _ng_glow_pulse 2.5s ease-in-out infinite;
    }
  `;
  document.head.appendChild(el);
}

/* ─── AgentThinkingIndicator ─── */

export function AgentThinkingIndicator() {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [phaseVisible, setPhaseVisible] = useState(true);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    const cycle = setInterval(() => {
      setPhaseVisible(false);
      const flip = setTimeout(() => {
        setPhaseIdx((p) => (p + 1) % PHASES.length);
        setPhaseVisible(true);
      }, 240);
      return () => clearTimeout(flip);
    }, 2500);
    return () => clearInterval(cycle);
  }, []);

  return (
    <div
      className="_ng_enter"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBlock: 4 }}
    >
      {/* Dot wave + phase label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Three animated orbs */}
        <div
          className="_ng_orb_glow"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 5,
            height: 22,
            padding: '2px 4px',
            borderRadius: 12,
          }}
        >
          <span
            className="_ng_d1"
            style={{
              display: 'block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#818cf8',
              flexShrink: 0,
            }}
          />
          <span
            className="_ng_d2"
            style={{
              display: 'block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #818cf8 0%, #2dd4bf 100%)',
              flexShrink: 0,
            }}
          />
          <span
            className="_ng_d3"
            style={{
              display: 'block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#2dd4bf',
              flexShrink: 0,
            }}
          />
        </div>

        {/* Cycling phase text */}
        <span
          key={phaseIdx}
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: '#94a3b8',
            opacity: phaseVisible ? 1 : 0,
            transition: 'opacity 0.22s ease',
            letterSpacing: '0.01em',
          }}
        >
          {PHASES[phaseIdx]}
        </span>
      </div>

      {/* Gradient shimmer progress bar */}
      <div
        style={{
          position: 'relative',
          height: 2,
          width: 72,
          borderRadius: 99,
          background: 'rgba(129,140,248,0.15)',
          overflow: 'hidden',
        }}
      >
        <div className="_ng_shimmer" />
      </div>
    </div>
  );
}

/* ─── StreamingCursor ─── */

export function StreamingCursor() {
  useEffect(() => {
    injectStyles();
  }, []);

  return <span className="_ng_cursor" aria-hidden="true" />;
}
